import React, { useState } from 'react';
import {
  BookOpen,
  Search,
  Filter,
  GraduationCap,
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  FileText,
  Star,
  TrendingUp,
  Users,
  Globe,
  Award,
  ChevronRight,
  Info,
  X
} from 'lucide-react';

interface Program {
  id: string;
  name: string;
  university: string;
  country: string;
  city: string;
  degree: string;
  duration: string;
  tuitionFee: string;
  language: string;
  intakeMonths: string[];
  requirements: string[];
  ranking?: number;
  description: string;
  category: string;
}

const ProgramsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterDegree, setFilterDegree] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);

  const programs: Program[] = [
    {
      id: '1',
      name: 'Computer Science',
      university: 'University of Milan',
      country: 'İtalya',
      city: 'Milan',
      degree: 'Lisans',
      duration: '3 yıl',
      tuitionFee: '€3,000/yıl',
      language: 'İngilizce',
      intakeMonths: ['Eylül', 'Şubat'],
      requirements: ['Lise Diploması', 'IELTS 6.5', 'Motivasyon Mektubu'],
      ranking: 45,
      description: 'Modern bilgisayar bilimi programı ile yazılım mühendisliği, yapay zeka ve veri bilimi alanlarında uzmanlaşın.',
      category: 'Mühendislik'
    },
    {
      id: '2',
      name: 'Business Administration',
      university: 'Bocconi University',
      country: 'İtalya',
      city: 'Milan',
      degree: 'Yüksek Lisans',
      duration: '2 yıl',
      tuitionFee: '€14,000/yıl',
      language: 'İngilizce',
      intakeMonths: ['Eylül'],
      requirements: ['Lisans Diploması', 'GMAT 600+', 'İş Deneyimi (tercih edilir)'],
      ranking: 15,
      description: 'Dünya çapında tanınan işletme yönetimi programı ile global kariyer fırsatlarına hazırlanın.',
      category: 'İşletme'
    },
    {
      id: '3',
      name: 'Medicine',
      university: 'Sapienza University',
      country: 'İtalya',
      city: 'Rome',
      degree: 'Lisans',
      duration: '6 yıl',
      tuitionFee: '€2,500/yıl',
      language: 'İngilizce',
      intakeMonths: ['Eylül'],
      requirements: ['Lise Diploması', 'IMAT Sınavı', 'Biyoloji/Kimya Bilgisi'],
      ranking: 28,
      description: 'Avrupa\'nın en prestijli tıp fakültelerinden birinde uluslararası standartlarda eğitim.',
      category: 'Sağlık'
    },
    {
      id: '4',
      name: 'Architecture',
      university: 'Politecnico di Milano',
      country: 'İtalya',
      city: 'Milan',
      degree: 'Yüksek Lisans',
      duration: '2 yıl',
      tuitionFee: '€3,900/yıl',
      language: 'İngilizce/İtalyanca',
      intakeMonths: ['Eylül'],
      requirements: ['Mimarlık Lisans Diploması', 'Portfolio', 'İtalyanca A2 (İtalyanca program için)'],
      ranking: 10,
      description: 'Dünyaca ünlü mimarlık okulu ile tasarım ve inovasyon alanında öncü olun.',
      category: 'Mimarlık & Tasarım'
    },
    {
      id: '5',
      name: 'International Relations',
      university: 'University of Bologna',
      country: 'İtalya',
      city: 'Bologna',
      degree: 'Yüksek Lisans',
      duration: '2 yıl',
      tuitionFee: '€3,200/yıl',
      language: 'İngilizce',
      intakeMonths: ['Eylül'],
      requirements: ['Lisans Diploması', 'IELTS 6.5', 'CV'],
      ranking: 35,
      description: 'Global politika ve diplomasi alanında uzmanlaşarak uluslararası kuruluşlarda kariyer yapın.',
      category: 'Sosyal Bilimler'
    },
    {
      id: '6',
      name: 'Fashion Design',
      university: 'Istituto Marangoni',
      country: 'İtalya',
      city: 'Milan',
      degree: 'Lisans',
      duration: '3 yıl',
      tuitionFee: '€20,000/yıl',
      language: 'İngilizce/İtalyanca',
      intakeMonths: ['Ekim', 'Şubat'],
      requirements: ['Lise Diploması', 'Portfolio', 'Yaratıcı Düşünme Testi'],
      description: 'Moda başkenti Milan\'da dünya çapında tanınan moda tasarımı eğitimi.',
      category: 'Mimarlık & Tasarım'
    }
  ];

  const countries = ['Tümü', 'İtalya', 'İspanya', 'Almanya', 'Fransa'];
  const degrees = ['Tümü', 'Lisans', 'Yüksek Lisans', 'Doktora'];
  const categories = ['Tümü', 'Mühendislik', 'İşletme', 'Sağlık', 'Mimarlık & Tasarım', 'Sosyal Bilimler'];

  const filteredPrograms = programs.filter(program => {
    const matchesSearch =
      program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.university.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCountry = filterCountry === 'all' || program.country === filterCountry;
    const matchesDegree = filterDegree === 'all' || program.degree === filterDegree;
    const matchesCategory = filterCategory === 'all' || program.category === filterCategory;

    return matchesSearch && matchesCountry && matchesDegree && matchesCategory;
  });

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Mühendislik': 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-200 dark:border-blue-700',
      'İşletme': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 border border-green-200 dark:border-green-700',
      'Sağlık': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border border-red-200 dark:border-red-700',
      'Mimarlık & Tasarım': 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200 border border-purple-200 dark:border-purple-700',
      'Sosyal Bilimler': 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 border border-orange-200 dark:border-orange-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">Program Rehberi</h1>
        <p className="text-lg text-gray-500 dark:text-gray-400 mt-2 font-medium">
          Öğrencileriniz için uygun programları keşfedin
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-6 text-white shadow-xl shadow-blue-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-blue-100 text-sm font-medium">Toplam Program</p>
            <p className="text-3xl font-bold tracking-tight mt-1">{programs.length}</p>
            <p className="text-blue-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">{countries.length - 1} ülke</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-6 text-white shadow-xl shadow-green-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-green-100 text-sm font-medium">Lisans Programları</p>
            <p className="text-3xl font-bold tracking-tight mt-1">
              {programs.filter(p => p.degree === 'Lisans').length}
            </p>
            <p className="text-green-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Program</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl p-6 text-white shadow-xl shadow-purple-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Award className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-purple-100 text-sm font-medium">Yüksek Lisans</p>
            <p className="text-3xl font-bold tracking-tight mt-1">
              {programs.filter(p => p.degree === 'Yüksek Lisans').length}
            </p>
            <p className="text-purple-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Program</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl p-6 text-white shadow-xl shadow-orange-500/20 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-orange-100 text-sm font-medium">Popüler Programlar</p>
            <p className="text-3xl font-bold tracking-tight mt-1">
              {programs.filter(p => p.ranking && p.ranking < 30).length}
            </p>
            <p className="text-orange-100 text-xs mt-2 bg-white/10 inline-block px-2 py-1 rounded-lg">Yüksek sıralama</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-2 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            placeholder="Program, üniversite veya kategori ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 font-medium"
          />
        </div>
        <div className="h-auto w-px bg-gray-200 dark:bg-gray-700 mx-2 hidden lg:block" />
        <div className="flex gap-2 p-1 overflow-x-auto">
          <select
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {countries.map(country => (
              <option key={country} value={country === 'Tümü' ? 'all' : country}>
                {country}
              </option>
            ))}
          </select>
          <select
            value={filterDegree}
            onChange={(e) => setFilterDegree(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {degrees.map(degree => (
              <option key={degree} value={degree === 'Tümü' ? 'all' : degree}>
                {degree}
              </option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {categories.map(category => (
              <option key={category} value={category === 'Tümü' ? 'all' : category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Program Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {filteredPrograms.map(program => (
          <div
            key={program.id}
            className="group bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 border border-gray-100 dark:border-gray-700 cursor-pointer relative overflow-hidden"
            onClick={() => setSelectedProgram(program)}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 dark:bg-gray-700/30 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-150 duration-500" />

            <div className="relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(program.category)}`}>
                      {program.category}
                    </span>
                    {program.ranking && (
                      <div className="flex items-center gap-1 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-full border border-yellow-200 dark:border-yellow-700">
                        <Star className="w-3 h-3 text-yellow-600 dark:text-yellow-400 fill-yellow-600 dark:fill-yellow-400" />
                        <span className="text-xs font-bold text-yellow-700 dark:text-yellow-400">
                          #{program.ranking}
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {program.name}
                  </h3>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <GraduationCap className="w-4 h-4" />
                    {program.university}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-700/50 flex items-center justify-center text-gray-400 group-hover:bg-blue-600 group-hover:text-white transition-all">
                  <ChevronRight className="w-5 h-5" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-2xl flex items-center gap-3">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                    <MapPin className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Konum</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{program.city}, {program.country}</p>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-2xl flex items-center gap-3">
                  <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                    <DollarSign className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Ücret</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{program.tuitionFee}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span>{program.degree} • {program.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-purple-500" />
                  <span>{program.language}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPrograms.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-12 text-center border border-dashed border-gray-300 dark:border-gray-700">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Program Bulunamadı</h3>
          <p className="text-gray-500 dark:text-gray-400">Aradığınız kriterlere uygun program bulunamadı.</p>
        </div>
      )}

      {selectedProgram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedProgram(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-t-3xl">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedProgram.name}
                </h2>
                <p className="text-base text-gray-600 dark:text-gray-400 font-medium mt-1">
                  {selectedProgram.university}
                </p>
              </div>
              <button
                onClick={() => setSelectedProgram(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center justify-center mb-3 text-red-500">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Konum</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedProgram.city}</span>
                  <span className="text-xs text-gray-500">{selectedProgram.country}</span>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center justify-center mb-3 text-blue-500">
                    <Clock className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Süre / Derece</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedProgram.duration}</span>
                  <span className="text-xs text-gray-500">{selectedProgram.degree}</span>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center justify-center mb-3 text-green-500">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Ücret</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedProgram.tuitionFee}</span>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl shadow-sm flex items-center justify-center mb-3 text-purple-500">
                    <Globe className="w-5 h-5" />
                  </div>
                  <span className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Dil</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{selectedProgram.language}</span>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                  Program Hakkında
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-800/30 p-6 rounded-2xl border border-gray-100 dark:border-gray-800">
                  {selectedProgram.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="w-1 h-6 bg-green-600 rounded-full"></div>
                    Başvuru Gereksinimleri
                  </h3>
                  <ul className="space-y-3">
                    {selectedProgram.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                        <div className="w-6 h-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <div className="w-1 h-6 bg-orange-600 rounded-full"></div>
                    Başvuru Dönemleri
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedProgram.intakeMonths.map((month, idx) => (
                      <div key={idx} className="px-4 py-2 bg-orange-50 dark:bg-orange-900/10 text-orange-700 dark:text-orange-300 border border-orange-100 dark:border-orange-900/30 rounded-xl font-semibold text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {month}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setSelectedProgram(null)}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Kapat
                </button>
                <button className="flex-[2] px-6 py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-shadow shadow-lg shadow-blue-500/20 active:scale-95">
                  Öğrenciye Öner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramsPage;
